import { useEffect, useRef } from "react";
import { rememberWorksheet } from "./RecentWorksheets.ts";
import { ISettingsProps } from "../MathFactsGenerator/Models.ts";

/** How long the settings must sit still before the sheet counts as made. */
export const SETTLE_MS = 4000;

/**
 * Remembers a worksheet once its settings have stopped changing.
 *
 * The trigger is deliberately a pause rather than every change. Someone
 * stepping the problem count from 20 to 25 to 30 has made one worksheet, not
 * three, and a list full of the states they passed through on the way is worse
 * than no list. Waiting for the settings to settle records where they stopped.
 *
 * The first render is skipped too, so merely opening a page does not file the
 * sheet it happened to open with - the list is meant to hold what someone
 * built, not what they were shown.
 *
 * `path` is the page the sheet is being made on, which is where its entry in
 * the list will reopen it.
 */
export function useRememberWorksheet(settings: ISettingsProps, seed?: string, path?: string) {
    const isFirstRender = useRef(true);
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        clearTimeout(timer.current);
        timer.current = setTimeout(() => rememberWorksheet(settings, seed, path), SETTLE_MS);

        return () => clearTimeout(timer.current);
    }, [settings, seed, path]);
}

export default useRememberWorksheet;
